import { equals } from 'ramda'
import { ref } from 'vue'
import type { Ref } from 'vue'

import { usePolls } from '@/composables/polls'
import {
  BUSINESS_SETUP_NO_STAFFERS_CHOICES,
  POLLS_GROUP_NAME,
  REGISTRATION_POLLS,
  type PollType,
} from './consts.ts'

type RegistrationPollId = (typeof REGISTRATION_POLLS)[keyof typeof REGISTRATION_POLLS]

type OnboardingPollChoice = {
  id: number | string
  internal_name: string
  choice?: string
}

export type OnboardingPoll = {
  name: string
  poll_type: PollType
  max_selections: number
  description?: string
  additional_description?: string
  savedChoices?: unknown[]
  choices?: OnboardingPollChoice[]
  savedInternalNamesChoices?: string[]
}

const onboardingPolls = ref<OnboardingPoll[] | null>(null)
const isBusinessWithoutStaffers = ref(false)

const REGISTRATION_POLLS_ORDER = [
  REGISTRATION_POLLS.OTHER_APPS,
  REGISTRATION_POLLS.BOOKSY_HELP,
  REGISTRATION_POLLS.BUSINESS_SETUP,
  REGISTRATION_POLLS.NUMBER_OF_CLIENTS,
] as const satisfies readonly RegistrationPollId[]

const registrationPollOrderSet = new Set<string>(REGISTRATION_POLLS_ORDER)

const businessSetupNoStaffersChoicesSet = new Set<string>(BUSINESS_SETUP_NO_STAFFERS_CHOICES)

export type UseOnboardingPollsReturn = {
  getPoll: (pollName: string) => OnboardingPoll | undefined
  loadPolls: () => Promise<void>
  onboardingPolls: Ref<OnboardingPoll[] | null>
  submitPoll: (args: { pollName: string; choices: unknown[] }) => Promise<void>
  REGISTRATION_POLLS: typeof REGISTRATION_POLLS
  isBusinessWithoutStaffers: Ref<boolean>
}

const resolveInternalNamesFromChoices = (
  poll: OnboardingPoll,
  choices: unknown[]
): string[] => {
  const matchedChoices: string[] = []
  choices.forEach(choice => {
    const matchingChoice = poll.choices?.find(item => item.id == choice)
    if (matchingChoice) {
      matchedChoices.push(matchingChoice.internal_name)
    }
  })
  return matchedChoices
}

const updateBusinessWithoutStaffersFromChoices = (
  pollName: string,
  poll: OnboardingPoll,
  choices: unknown[]
) => {
  if (pollName !== REGISTRATION_POLLS.BUSINESS_SETUP) return

  const selectedInternalNames = resolveInternalNamesFromChoices(poll, choices)
  isBusinessWithoutStaffers.value = selectedInternalNames.some(internalName =>
    businessSetupNoStaffersChoicesSet.has(internalName)
  )
}

const hydrateBusinessWithoutStaffersFromLoadedPolls = () => {
  const setupPoll = onboardingPolls.value?.find(
    poll => poll.name === REGISTRATION_POLLS.BUSINESS_SETUP
  )
  if (!setupPoll?.savedChoices?.length) {
    isBusinessWithoutStaffers.value = false
    return
  }
  updateBusinessWithoutStaffersFromChoices(
    REGISTRATION_POLLS.BUSINESS_SETUP,
    setupPoll,
    setupPoll.savedChoices
  )
}

export function useOnboardingPolls (): UseOnboardingPollsReturn {
  const { loadPollsList, savePoll } = usePolls()

  const loadPolls = async () => {
    const allPolls = (await loadPollsList(POLLS_GROUP_NAME)) as OnboardingPoll[] | null | undefined
    onboardingPolls.value = allPolls?.filter(poll => registrationPollOrderSet.has(poll.name)) ?? null
    hydrateBusinessWithoutStaffersFromLoadedPolls()
  }

  const getPoll = (pollName: string): OnboardingPoll | undefined => {
    return onboardingPolls.value?.find(poll => poll.name === pollName)
  }

  const submitPoll = async ({ pollName, choices }: { pollName: string; choices: unknown[] }) => {
    try {
      const poll = getPoll(pollName)
      if (!poll || equals(poll.savedChoices, choices)) return

      await savePoll({ pollName, choices })
      poll.savedChoices = choices
      poll.savedInternalNamesChoices = resolveInternalNamesFromChoices(poll, choices)

      updateBusinessWithoutStaffersFromChoices(pollName, poll, choices)
      return Promise.resolve()
    } catch (err: unknown) {
      const response = err && typeof err === 'object' && 'response' in err
        ? (err as { response: { data: unknown } }).response
        : undefined
      return Promise.reject(response?.data)
    }
  }

  return {
    getPoll,
    loadPolls,
    onboardingPolls,
    submitPoll,
    REGISTRATION_POLLS,
    isBusinessWithoutStaffers,
  }
}
